import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { BookingTypeValues } from '@components/BookingType';
import {
  roomsFilters,
  ROUTE_ABS_DOLLHOUSE,
  ROUTE_ABS_DOLLHOUSE_FLOORPLAN,
  ROUTE_ABS_HOTEL,
  ROUTE_ABS_HOTEL_GUEST_ROOMS,
  ROUTE_ABS_ROOT,
} from '@constants';
import { usePostMessageListener } from '@hooks/usePostMessageListener';
import { generatePath, useLocation, useNavigate, useSearchParams } from '@router';
import { normalize } from '@spazious/jsonapi-tools';
import { useGetCities, useGetHotels } from '@spazious/react-booking-engine';
import { IframeEventName, InitConfigPayload, PostMessageType } from '@spazious/search-sdk-types';
import postInteractionEvent from '@utils/sdkEvents';

import { SearchFormContext } from './SearchFormContext.js';
import { SearchFormActionKind, SearchFormState, useSearchFormStore } from './SearchFormStore.js';
import { SearchFormDestinationType } from './SearchFormTypes.js';
import {
  formatSearchParams,
  parseSearchParams,
  parseSlugPayloadToDestination,
  parseSuggestToDestination,
} from './SearchFormUtils.js';

export type SearchFormProviderProps = {
  children?: React.ReactNode;
  hotelChainSlug: string;
};

const patternReplacement = (pattern: string, values: Record<string, string>) => {
  return pattern.replace(/\{([^}]+)\}/g, (_, key) => String(values[key] ?? ''));
};

const getWidgetRedirectBasePath = (hotelChainSlug: string) => {
  return patternReplacement(import.meta.env.SPZ_SEARCH_REDIRECT_URL ?? '', {
    hotelChainSlug,
  });
};

export const SearchFormProvider: React.FC<
  SearchFormProviderProps & {
    onReady?: () => void;
  }
> = ({ children, hotelChainSlug, onReady }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const [messageParams, setMessageParams] = useState<Partial<
    SearchFormState['destination']
  > | null>(null);
  const routerState = useMemo(() => {
    const parsedSearchParams = parseSearchParams(searchParams);
    const destination = messageParams || parsedSearchParams.destination;

    const state = {
      ...parsedSearchParams,
      ...(destination ? { destination } : {}),
    };
    return state;
  }, [searchParams, messageParams]);

  const initialState: Partial<SearchFormState> = useMemo(() => {
    return {
      ...routerState,
      destination: routerState.destination || location?.state?.destination,
    };
  }, [routerState, location]);
  const internalStore = useSearchFormStore(initialState);
  const [, , store] = internalStore;

  const [{ bookingType: bookingTypeValue }] = internalStore;

  const [{ destination: destinationStore }, { setState }] = internalStore;

  useLayoutEffect(() => {
    return store.subscribe((action) => {
      const state = store.getState();

      if (
        action.type === SearchFormActionKind.SetDestination ||
        action.type === SearchFormActionKind.DoSearch
      ) {
        const destination =
          action.type === SearchFormActionKind.SetDestination ? action.payload : state.destination;
        const search = state.dateRange ? formatSearchParams(state, destination) : undefined;

        const pathnames = {
          [SearchFormDestinationType.Hotel]:
            bookingTypeValue === BookingTypeValues.OnlySpaces
              ? ROUTE_ABS_HOTEL
              : ROUTE_ABS_HOTEL_GUEST_ROOMS,
          [SearchFormDestinationType.Location]: ROUTE_ABS_ROOT,
          [SearchFormDestinationType.DollhouseLocation]: ROUTE_ABS_DOLLHOUSE,
          [SearchFormDestinationType.DollhouseHotel]: ROUTE_ABS_DOLLHOUSE_FLOORPLAN,
        };

        if (action.type === SearchFormActionKind.DoSearch && search && destination?.type) {
          const basePath = getWidgetRedirectBasePath(hotelChainSlug);
          const url = `${generatePath(pathnames[destination.type], destination)}${search}`;
          postInteractionEvent(IframeEventName.SEARCH_SUBMIT, { url: basePath + url });
        }
      }
    });
  }, [bookingTypeValue, hotelChainSlug, navigate, setState, store]);

  const { data: hotelsQueryResult } = useGetHotels({
    variables: {
      include: ['brand', 'country', 'city'],
      filters: {
        hotelChainSlug: hotelChainSlug,
        ...(bookingTypeValue ? roomsFilters[bookingTypeValue] : {}),
      },
      pageSize: 100,
    },
    skip: !hotelChainSlug,
  });

  const hotelsData = useMemo(
    () => (hotelsQueryResult ? normalize(hotelsQueryResult) : []),
    [hotelsQueryResult]
  );

  const { data: citiesQueryResult } = useGetCities({
    variables: {
      include: ['country'],
      filters: {
        hotelChainSlug: hotelChainSlug,
        ...(bookingTypeValue ? roomsFilters[bookingTypeValue] : {}),
      },
      pageSize: 100,
    },
    skip: !hotelChainSlug,
  });

  const citiesData = useMemo(
    () => (citiesQueryResult ? normalize(citiesQueryResult) : []),
    [citiesQueryResult]
  );

  // Sync router state with store using location as reference for updating
  useLayoutEffect(() => {
    const { destination, ...restRouterState } = routerState;

    const newState = destination
      ? {
          destination,
          ...restRouterState,
        }
      : restRouterState;

    if (!destination) {
      return;
    }

    // Check if destination needs to be set up or updated
    const needsDestinationSetup =
      !destinationStore ||
      (destinationStore.type === SearchFormDestinationType.Location &&
        !destinationStore.cityId &&
        citiesData.length > 0);

    if (needsDestinationSetup) {
      let foundItem = null;
      if (destination.type === SearchFormDestinationType.Location) {
        foundItem = citiesData.find(
          (city) =>
            destination.citySlug === city.slug && destination.countrySlug === city.country.slug
        );
      } else {
        foundItem = hotelsData?.find((hotel) => hotel.slug === destination?.hotelSlug);
      }
      if (foundItem) {
        setState({
          ...newState,
          destination: parseSuggestToDestination(foundItem),
        });
      } else {
        setState(newState);
      }
    } else {
      // Destination is already set up, just update non-destination fields (bookingType, dateRange, capacityOrRoomCount)
      setState(restRouterState);
    }
  }, [hotelChainSlug, hotelsData, location, destinationStore, routerState, setState, citiesData]);

  usePostMessageListener<InitConfigPayload>(
    PostMessageType.INIT_CONFIG,
    useCallback((payload) => {
      const destinationPayload = payload?.destination;
      if (!destinationPayload) return;

      const { destination } = parseSlugPayloadToDestination(destinationPayload);
      if (destination) {
        setMessageParams(destination);
      }
    }, [])
  );

  useEffect(() => {
    if (!messageParams) return;
    if (!hotelsData.length && !citiesData.length) {
      return;
    }

    let foundItem = null;

    if (messageParams.type === SearchFormDestinationType.Location) {
      foundItem = citiesData.find(
        (city) =>
          city.slug === messageParams.citySlug && city.country.slug === messageParams.countrySlug
      );
    } else if (messageParams.type === SearchFormDestinationType.Hotel) {
      foundItem = hotelsData.find((hotel) => hotel.slug === messageParams.hotelSlug);
    }

    const finalDestination = foundItem ? parseSuggestToDestination(foundItem) : messageParams;

    setState((prev) => ({
      ...prev,
      destination: finalDestination,
    }));

    setMessageParams(null);
  }, [messageParams, hotelsData, citiesData, setState]);

  useEffect(() => {
    const dataReady = hotelsData.length > 0 || citiesData.length > 0;
    if (dataReady && !messageParams) {
      onReady?.();
    }
  }, [hotelsData, citiesData, messageParams, onReady]);

  return <SearchFormContext.Provider value={internalStore}>{children}</SearchFormContext.Provider>;
};

export default SearchFormProvider;
